import {
  ImageLoader,
  LinearFilter,
  LinearMipMapLinearFilter,
  RepeatWrapping,
  Texture,
  TextureLoader,
} from "three";
import { WebGPURendererSingleton } from "../engine/renderer/WebGPURendererSingleton";
import { KTX2LoaderSingleton } from "./KTX2LoaderSingleton";
import { Loader } from "./Loader";

function textureLoadError(url: string, cause?: unknown): Error {
  const error = new Error(`Failed to load texture: ${url}`);
  if (cause !== undefined) {
    (error as Error & { cause?: unknown }).cause = cause;
  }
  return error;
}

function maxAnisotropy(): number {
  try {
    return WebGPURendererSingleton.getInstance().instance.getMaxAnisotropy();
  } catch {
    return 1;
  }
}

export class SugarTextureLoader implements Loader {
  private textureLoader: TextureLoader | undefined;

  public constructor() {}

  getKtxLoader() {
    return KTX2LoaderSingleton.getInstance();
  }

  getTextureLoader() {
    if (!this.textureLoader) {
      this.textureLoader = new TextureLoader();
    }
    return this.textureLoader;
  }

  load(key: string) {
    const promise = new Promise((resolve, reject) => {
      const texture = new Texture();
      const url = key;

      if (url.match(/.ktx2$|.ktx2\?.*/gm)) {
        void KTX2LoaderSingleton.ensureSupportDetected()
          .then(() => {
            const ktxLoader = this.getKtxLoader();
            ktxLoader.load(
              url,
              (loaded) => {
                loaded.anisotropy = maxAnisotropy();
                loaded.wrapS = RepeatWrapping;
                loaded.wrapT = RepeatWrapping;
                loaded.minFilter = LinearMipMapLinearFilter;
                loaded.magFilter = LinearFilter;
                loaded.needsUpdate = true;
                resolve(loaded);
              },
              undefined,
              (cause) => reject(textureLoadError(url, cause)),
            );
          })
          .catch((cause) => reject(textureLoadError(url, cause)));
      } else {
        const imageLoader = new ImageLoader(this.getTextureLoader().manager);

        imageLoader.setCrossOrigin(this.getTextureLoader().crossOrigin);
        imageLoader.setPath(this.getTextureLoader().path);

        imageLoader.load(
          url,
          function (image) {
            texture.image = image;
            texture.wrapS = RepeatWrapping;
            texture.wrapT = RepeatWrapping;
            texture.anisotropy = maxAnisotropy();
            texture.minFilter = LinearMipMapLinearFilter;
            texture.magFilter = LinearFilter;
            texture.needsUpdate = true;
            resolve(texture);
          },
          undefined,
          (cause) => reject(textureLoadError(url, cause)),
        );
      }
    });
    return promise;
  }
}
